import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination } from './ui';
import {
  useC360List, C360Table, ColumnChooser, ViewIcon, EditIcon,
} from './c360';
import { Icon } from './detail/DetailShell';
import { printLayout } from './belgeDizayni/printRenderer';
import { usePageActions } from '../contexts/PermissionContext';
import {
  STATUS, TypeIcon, getType, showsDueDateColumn, GROUP,
} from './finance/financeTypeMeta';
import './finance/financeScreens.css';

const API_BASE = '/api/financetransactions';
const TEMPLATE_API = '/api/documenttemplates';
const CUSTOMER_API = '/api/customers';
const DOC_TYPE_FINANCE = 3; // Belge Dizaynı: "Tahsilat" tasarımı

const getRows = (data) => (Array.isArray(data) ? data : (data?.value || []));

const fmtMoney = (v) =>
  (Number(v) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (v) => (Number(v) || 0).toLocaleString('tr-TR');
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('tr-TR') : '-');

/** OData string literal kaçışı — tek tırnak ikilenir. */
const odataStr = (v) => String(v).replace(/'/g, "''").toLowerCase();

/** "1.234,56" / "1234.56" → sayı; değilse null (tutarla arama için). */
function parseAmount(text) {
  const t = String(text || '').trim().replace(/\./g, '').replace(',', '.');
  if (!t || !/^\d+(\.\d+)?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * Cari hareket listesi (tahsilat / ödeme) — TAHSILAT_ODEME.md §2.
 *
 * İskelet: menü rozeti başlığı → 4 stat kartı → arama → veri grid'i → alt not.
 * Üstte "Tümü + tür" segment sekmesi YOKTUR; tür SOL MENÜDEN seçilir ve rotayla gelir.
 *
 * Props:
 *   group : 'collection' | 'payment'   (Alınan Tahsilatlar / Ödemelerim)
 *   slug  : 'cash' | 'bank-transfer' | 'credit-card' | 'cheque' | 'promissory-note' |
 *           'dbs' | 'debit-entry' | 'credit-entry'
 */
export function FinanceTransactionsPage({ group, slug }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { canAdd, canEdit } = usePageActions();

  const type = getType(group, slug);
  const groupMeta = GROUP[group];
  const currentListRoute = location.pathname;
  const showDueDate = type ? showsDueDateColumn(type) : false;

  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [customerMap, setCustomerMap] = useState({}); // customerId -> { code, name }
  const [summary, setSummary] = useState(null);
  // Arama kutusu useC360List'in `searchFields`i yerine baseFilter'a örülür: spec "Belge no /
  // müşteri / tutar" istiyor ve tutar SAYISAL bir kolon — `contains(tolower(Amount))` OData'da
  // geçersizdir. Kendi cümlemizi kurunca tutarı `eq` ile, müşteriyi navigasyon üzerinden
  // arayabiliyoruz.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const showToast = (type_, message) => {
    setToast({ type: type_, message });
    setTimeout(() => setToast(null), 3500);
  };

  const baseFilter = useMemo(() => {
    if (!type) return 'Id eq null';
    const clauses = [`Direction eq ${type.direction}`, `Method eq ${type.method}`];
    const q = searchTerm.trim();
    if (q) {
      const t = odataStr(q);
      const ors = [
        `contains(tolower(DocumentNo),'${t}')`,
        `contains(tolower(Description),'${t}')`,
        `contains(tolower(Customer/Title),'${t}')`,
        `contains(tolower(Customer/TextId),'${t}')`,
      ];
      const amount = parseAmount(q);
      if (amount !== null) ors.push(`Amount eq ${amount}`);
      clauses.push(`(${ors.join(' or ')})`);
    }
    return clauses.join(' and ');
  }, [type, searchTerm]);

  // Kolon filtresi yok (§2.3: arama kutusunun yanında filtre-kriter butonu YOKTUR); boş meta
  // C360Table'a "başlık filtresi çizme" der. Arama cümlesini baseFilter'da kendimiz kurduğumuz
  // için useC360List'in kendi `searchFields` mekanizması devre dışı bırakılır.
  const COLUMN_FILTER_META = useMemo(() => ({}), []);
  const NO_SEARCH_FIELDS = useMemo(() => [], []);

  // ⚠ $select KULLANILMAZ, $expand kullanılır (useC360List: ikisi birlikte kullanılmamalı).
  // Vade artık başlıkta değil, enstrüman künyesinde (FinanceInstrument — TAHSILAT_ODEME.md §5.2);
  // $expand olmadan Vade kolonu boş gelirdi. Genişletme YALNIZ vadesi olan türlerde yapılır:
  // nakit/KK/manuel listelerinde künye zaten yoktur, gereksiz JOIN atılmaz.
  const expand = showDueDate ? 'Instrument' : '';

  const list = useC360List({
    apiBase: API_BASE,
    baseFilter,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: NO_SEARCH_FIELDS,
    defaultSortField: 'TransactionDate',
    defaultSortDir: 'desc',
    expand,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData,
  } = list;

  // ── Stat kartları (§2.2) — sunucu tarafında tek turda hesaplanır, listeyle AYNI kapsamdan ──
  useEffect(() => {
    if (!type) return undefined;
    let alive = true;
    fetch(`${API_BASE}/Summary?direction=${type.direction}&method=${type.method}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!alive) return;
        setSummary(json?.data ?? json?.Data ?? null);
      })
      .catch(() => { if (alive) setSummary(null); });
    return () => { alive = false; };
  }, [type]);

  // ── Müşteri adları — görünen satırların id'leri topluca çözülür ────────────────────────────
  // Customer OData'sında $select 500 verdiği için (bkz. customer-geo-columns-swapped) satır
  // projeksiyonu değil, id → ad sözlüğü kurulur.
  useEffect(() => {
    const ids = Array.from(new Set(
      rows.map((r) => r.customerId || r.CustomerId).filter((id) => id && !customerMap[id]),
    ));
    if (ids.length === 0) return;
    const filter = ids.map((id) => `Id eq ${id}`).join(' or ');
    fetch(`${CUSTOMER_API}?$filter=${encodeURIComponent(filter)}&$top=${ids.length}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { value: [] }))
      .then((data) => {
        const next = {};
        getRows(data).forEach((c) => {
          next[c.id || c.Id] = {
            code: c.TextId || c.textId || '',
            name: c.Title || c.title || '(adsız)',
          };
        });
        if (Object.keys(next).length) setCustomerMap((m) => ({ ...m, ...next }));
      })
      .catch(() => { /* yoksay — hücre "…" kalır */ });
  }, [rows, customerMap]);

  // ── Navigasyon ────────────────────────────────────────────────────────────────────────────
  const openNew = () => navigate(`${currentListRoute}/new`);
  const openDetail = (row) => {
    if (!row) return;
    setSelected(row);
    navigate(`${currentListRoute}/${row.id}`, { state: { row } });
  };
  const openEditRow = (row) => {
    if (!row) return;
    setSelected(row);
    navigate(`${currentListRoute}/${row.id}?edit=1`, { state: { row } });
  };

  // Yazdırma: Belge Dizaynı'ndaki "Tahsilat" (DocType 3) şablonunu kullanır.
  const handlePrint = async (row) => {
    try {
      const tRes = await fetch(
        `${TEMPLATE_API}?$filter=${encodeURIComponent(`DocType eq ${DOC_TYPE_FINANCE} and IsActive eq true`)}&$top=25`,
        { headers: authHeaders() },
      );
      const tpls = tRes.ok ? getRows(await tRes.json()) : [];
      const tpl = tpls.find((t) => t.isDefault || t.IsDefault) || tpls[0];
      const layoutJson = tpl && (tpl.layoutJson || tpl.LayoutJson);
      if (!layoutJson) {
        showToast('error', "Yazdırmak için Belge Dizaynı'nda 'Tahsilat' türünde bir şablon oluşturup kaydedin.");
        return;
      }
      const rRes = await fetch(`${TEMPLATE_API}/ResolveData?docType=${DOC_TYPE_FINANCE}&documentId=${row.id}`, { headers: authHeaders() });
      if (!rRes.ok) { showToast('error', 'Belge verisi alınamadı — sunucunun güncel sürümü çalışıyor mu?'); return; }
      const json = await rRes.json();
      const data = json?.data ?? json?.Data ?? json;
      printLayout(JSON.parse(layoutJson), data, type?.title || 'Cari Hareket');
    } catch (e) {
      showToast('error', e.message || 'Yazdırma sırasında hata oluştu');
    }
  };

  // ── Kolonlar (§2.4): Belge No · Tarih · Müşteri · Tür · Tutar · [Vade] · Durum · İşlem ────
  const columnHelper = createColumnHelper();
  const columns = useMemo(() => {
    const cols = [
      columnHelper.accessor('documentNo', {
        header: 'Belge No', enableColumnFilter: false, enableHiding: false,
        cell: (info) => <span className="fin-code">{info.getValue() || '-'}</span>,
      }),
      columnHelper.accessor('transactionDate', {
        header: 'Tarih', enableColumnFilter: false,
        cell: (info) => fmtDate(info.getValue()),
      }),
      columnHelper.display({
        id: 'customer', header: 'Müşteri', enableHiding: false,
        cell: ({ row }) => {
          const id = row.original.customerId || row.original.CustomerId;
          if (!id) return '-';
          const c = customerMap[id];
          if (!c) return '…';
          return (
            <span>
              <b style={{ color: 'var(--card-text-strong)' }}>{c.name}</b>
              {c.code && <span style={{ color: 'var(--text-secondary)', marginLeft: 8 }}>{c.code}</span>}
            </span>
          );
        },
      }),
      // Tür kolonu ekranın tamamında sabittir ama §2.4 gereği görünür durur: renkli pill,
      // ikon + metin birlikte (renk tek başına anlam taşımaz).
      columnHelper.display({
        id: 'type', header: 'Tür',
        cell: () => (
          <span className={`fin-type-pill fin-tone-${type.tone}`}>
            <TypeIcon name={type.icon} size={14} />{type.typeLabel}
          </span>
        ),
      }),
      columnHelper.accessor('amount', {
        header: 'Tutar', enableColumnFilter: false,
        cell: (info) => (
          <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {fmtMoney(info.getValue())} ₺
          </span>
        ),
      }),
    ];
    // Vade YALNIZ Çek/Senet'te render edilir — diğerlerinde kolon hiç çizilmez (§8).
    // Değer artık başlıkta değil, $expand=Instrument ile gelen künyededir (§5.2).
    if (showDueDate) {
      cols.push(columnHelper.display({
        id: 'dueDate', header: 'Vade',
        cell: ({ row }) => {
          const inst = row.original.instrument || row.original.Instrument;
          return fmtDate(inst?.dueDate ?? inst?.DueDate);
        },
      }));
    }
    cols.push(columnHelper.accessor('status', {
      header: 'Durum', enableColumnFilter: false,
      cell: (info) => {
        const cancelled = info.getValue() === STATUS.cancelled;
        // Durum hem renk hem KELİMEYLE (§8).
        return (
          <span style={{ color: cancelled ? 'var(--danger)' : 'var(--success)', fontWeight: 600 }}>
            {cancelled ? 'İptal' : 'İşlendi'}
          </span>
        );
      },
    }));
    cols.push(columnHelper.display({
      id: 'actions', header: 'İşlem', enableHiding: false,
      cell: ({ row }) => {
        const r = row.original;
        const cancelled = r.status === STATUS.cancelled;
        return (
          <span className="c360-actions">
            <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="İzle" onClick={(e) => { e.stopPropagation(); openDetail(r); }}><ViewIcon /></button>
            {canEdit && !cancelled && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Düzenle" onClick={(e) => { e.stopPropagation(); openEditRow(r); }}><EditIcon /></button>}
            <button type="button" className="c360-action-btn c360-action-btn--icon" title="Yazdır" onClick={(e) => { e.stopPropagation(); handlePrint(r); }}>🖨</button>
          </span>
        );
      },
    }));
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerMap, type, showDueDate, canEdit]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('TransactionDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const applySearch = (value) => { setSearchTerm(value); setCurrentPage(1); };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Belge no / müşteri / tutar"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applySearch(searchInput); }}
      />
      {searchTerm && <button style={s.btnSecondary} onClick={() => { setSearchInput(''); applySearch(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  if (!type) {
    return <div style={s.container}>Tanımsız finans türü: {group}/{slug}</div>;
  }

  const statCards = [
    { key: 'today', label: 'Bugün', tone: 'blue', data: summary?.today },
    { key: 'week', label: 'Bu Hafta', tone: 'purple', data: summary?.week },
    { key: 'month', label: 'Bu Ay', tone: 'lblue', data: summary?.month },
    // 4. kart "Bu Ay"ı TEKRAR ETMEZ: net Toplam (Çek/Senet'te Portföy) — etiketi sunucu verir.
    { key: 'total', label: summary?.total?.label || 'Toplam', tone: type.tone, data: summary?.total, isTotal: true },
  ];

  return (
    <div style={s.container}>
      {/* §2.1 — menü rozeti başlığı: renkli tür rozeti + başlık + tek satır açıklama */}
      <div className={`fin-pagehead fin-tone-${type.tone}`}>
        <span className="fin-rozet">
          <TypeIcon name={type.icon} size={19} />{type.typeLabel}
        </span>
        <div className="fin-pagehead-txt">
          <div className="fin-ttl">{type.title}</div>
          <div className="fin-sub">{type.subtitle}</div>
        </div>
        {canAdd && (
          <button style={{ ...newRecordButtonStyle, ...{ marginLeft: 'auto' } }} onClick={openNew}>
            + Yeni {groupMeta.word}
          </button>
        )}
      </div>

      {/* §2.2 — 4 stat kartı, türe göre tutarlı: Bugün ≤ Bu Hafta ≤ Bu Ay ≤ Toplam/Portföy */}
      <div className="fin-stats">
        {statCards.map((c) => (
          <div key={c.key} className={`fin-stat fin-tone-${c.tone}`}>
            <div className="fin-stat-l">{c.label}</div>
            <div className="fin-stat-v">₺ {fmtMoney(c.data?.amount)}</div>
            <div className="fin-stat-m">
              {c.isTotal ? '' : `${type.typeLabel} · `}
              {fmtInt(c.data?.count)} {type.unit}
            </div>
          </div>
        ))}
      </div>

      <ListPageShell
        title={`${type.title} Listesi`}
        subtitle={`${groupMeta.section} · müşteri bazlı hareketler`}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={{}}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {/* §2.5 — kasa/banka/muhasebe fişi BİLİNÇLİ olarak yoktur; not bunu görünür kılar. */}
      <div className="fin-hint">
        <Icon name="info" size={15} />
        <span>
          Kayıt yalnızca <b>cari hareket</b> oluşturur — kasa / banka / muhasebe fişi takibi tutulmaz.
        </span>
      </div>

      <Toast toast={toast} />
    </div>
  );
}
